import { isNil, omitBy } from "lodash-es";
import { useCallback, useEffect, useRef } from "react";

import {
  MonitoringEventContextDataMap,
  MonitoringEventType,
  MonitoringEventTypeType,
} from "../../shared/types/monitoring";

import { serverPostMonitoringEvent } from "api/api-monitoring";
import { store, useAppSelector } from "store/index";
import { selectCurrentCourseId, selectCurrentUserId } from "store/selectors/base";
import {
  selectActiveClassSessionId,
  selectIsClassSessionVirtual,
} from "store/selectors/class-session";
import { selectCurrentClassUserId } from "store/selectors/class-user";
import {
  selectCurrentCourseCodeTitle,
  selectCurrentCourseTitle,
  selectUserById,
} from "store/selectors/course";
import { selectCurrentOrg } from "store/selectors/org";
import { useViewLevel } from "utils/urls";
import { getUserFullName } from "utils/user-utils";

// Type alias for easier ESLint allowlisting
type CreateMonitoringEventFunction = <T extends keyof MonitoringEventContextDataMap>(
  eventType: T,
  contextData: MonitoringEventContextDataMap[T],
) => void;

interface BufferedEvent {
  eventType: MonitoringEventTypeType;
  contextData: MonitoringEventContextDataMap[MonitoringEventTypeType];
}

// Get store data at runtime instead of using selectors to avoid re-renders and avoid
// events to be sent multiple times.
const getStoreData = () => {
  const state = store.getState();
  const classId = selectActiveClassSessionId(state);
  const isVirtualClass = selectIsClassSessionVirtual(state);
  const defaultCourseId = selectCurrentCourseId(state);
  const org = selectCurrentOrg(state);
  const userId = selectCurrentUserId(state);
  const user = selectUserById(state, userId);
  const userName = getUserFullName(user);
  const classUserId = selectCurrentClassUserId(state);
  const courseTitle = selectCurrentCourseTitle(state);
  const courseCode = selectCurrentCourseCodeTitle(state);

  return omitBy(
    {
      classId,
      isVirtualClass,
      defaultCourseId,
      org,
      userId,
      classUserId,
      userName,
      courseTitle,
      courseCode,
    },
    isNil,
  );
};

// Check if store has all required data for monitoring events
const isStoreReady = () => {
  const { defaultCourseId, org, userId, classId, classUserId } = getStoreData();
  return !!(defaultCourseId && org && userId && classId && classUserId);
};

const sendMonitoringEvent = (
  eventType: MonitoringEventTypeType,
  contextData: MonitoringEventContextDataMap[MonitoringEventTypeType],
) => {
  const { defaultCourseId, isVirtualClass, org, ...rest } = getStoreData();
  const courseId =
    "courseId" in contextData && !!contextData.courseId ? contextData.courseId : defaultCourseId;
  serverPostMonitoringEvent({
    type: eventType,
    contextData,
    classType: isVirtualClass ? "virtual" : "in-person",
    courseId,
    orgId: org.id,
    orgShortName: org.short_name,
    ...rest,
  } as MonitoringEventType);
};

/**
 * Hook for creating and sending monitoring events with buffering.
 *
 * This hook implements a buffering mechanism to handle monitoring events that need to be sent before the Redux store
 * is fully initialized. The implementation addresses several key challenges:
 *
 * **Problem**: Monitoring events often need to be sent early in the application lifecycle
 * (e.g., page loads, component mounts) when Redux store data (courseId, userId, classId, etc.)
 * may not yet be available.
 *
 * **Solution**:
 * 1. **Immediate sending**: If store data is available, events are sent immediately
 * 2. **Buffering**: If store data is missing, events are buffered in a ref-based queue
 * 3. **Automatic flushing**: When store becomes ready (detected via useEffect dependencies),
 *    all buffered events are sent in FIFO order
 *
 * **Key Design Decisions**:
 * - Uses `getStoreData()` function instead of selectors in the createMonitoringEvent callback to ensure events
 * aren't sent multiple times
 * - Monitors specific store values via useEffect dependencies to detect when store is ready
 * - Processes buffered events in FIFO order to maintain chronological accuracy

 * @returns A memoized function that creates monitoring events with automatic buffering
 */
export const useCreateMonitoringEvent = () => {
  const { monitoring } = useViewLevel();

  // Buffer for all events that can't be sent immediately
  const eventBuffer = useRef<BufferedEvent[]>([]);

  const currentCourseId = useAppSelector(selectCurrentCourseId);
  const currentOrg = useAppSelector(selectCurrentOrg);
  const currentUserId = useAppSelector(selectCurrentUserId);
  const currentClassId = useAppSelector(selectActiveClassSessionId);
  const currentIsVirtualClass = useAppSelector(selectIsClassSessionVirtual);
  const currentClassUserId = useAppSelector(selectCurrentClassUserId);

  // Flush buffered events when store becomes ready. To know when the store is ready, we use the selector values in the
  // useEffect dependencies.
  useEffect(() => {
    if (isStoreReady() && eventBuffer.current.length > 0) {
      // Process buffered events in first-in-first-out order
      const eventsToProcess = [...eventBuffer.current];
      eventBuffer.current = [];

      for (const bufferedEvent of eventsToProcess) {
        sendMonitoringEvent(bufferedEvent.eventType, bufferedEvent.contextData);
      }
    }
  }, [
    currentClassId,
    currentCourseId,
    currentIsVirtualClass,
    currentOrg?.id,
    currentUserId,
    currentClassUserId,
  ]);

  const createMonitoringEvent: CreateMonitoringEventFunction = useCallback(
    <T extends keyof MonitoringEventContextDataMap>(
      eventType: T,
      contextData: MonitoringEventContextDataMap[T],
    ) => {
      // We don't need to create monitoring events from the monitoring dashboard
      if (monitoring) return;

      // If store is ready, send immediately
      if (isStoreReady()) {
        sendMonitoringEvent(eventType, contextData);
        return;
      }

      eventBuffer.current.push({
        eventType,
        contextData,
      });
    },
    [monitoring],
  );

  return createMonitoringEvent;
};
